"use client";

import type { DiscoverModel } from "@/app/_api/elevenlabs/fetchModelPageData";

import { AspectRatio } from "@ui/components/core/AspectRatio";
import { BaseLink } from "@ui/components/core/Link";
import { CardLinkArrow } from "@ui/components/core/CardLinkArrow";
import { Noise } from "@ui/components/core/Noise/Noise";
import { IconImage, IconVideo } from "@ui/components/demos/ImageVideoHero/components";
import { sizes } from "@ui/lib/sizes";
import { logger } from "@utilities/logger";
import Image from "next/image";
import { useRef } from "react";
import { useHover } from "react-aria";
import { ScrollGrid } from "@ui/components/layout/ScrollGrid";

interface DiscoverModelsCarouselProps {
  models: DiscoverModel[];
}

const MIN_CARDS = 3;

export function DiscoverModelsCarousel({ models }: DiscoverModelsCarouselProps) {
  const placeholderCount = Math.max(0, MIN_CARDS - models.length);
  const cards: React.ReactNode[] = [
    ...models.map(model => <DiscoverModelCard key={model.slug} model={model} />),
    ...Array.from({ length: placeholderCount }, (_, i) => <PlaceholderCard key={`placeholder-${i}`} />),
  ];

  return <ScrollGrid items={cards} renderItem={card => card} />;
}

function PlaceholderCard() {
  return <AspectRatio ratio='3x4' rounded='xl' centered={false} className='tw-bg-cream' />;
}

function DiscoverModelCard({ model }: { model: DiscoverModel }) {
  const { humanReadableName, shortDescription, modality, slug, thumbnailUrl, contentUrl, blurDataURL } = model;
  const isVideo = modality === "video" && !!contentUrl;
  const videoRef = useRef<HTMLVideoElement>(null);

  const { hoverProps } = useHover({
    onHoverStart: () => {
      const video = videoRef.current;
      if (!video) return;
      video.play().catch(err => {
        logger.error(err instanceof Error ? err : null, "[DiscoverModelsCarousel] Video hover play failed");
      });
    },
    onHoverEnd: () => {
      const video = videoRef.current;
      if (!video) return;
      video.pause();
      video.currentTime = 0;
    },
  });

  return (
    <div className='tw-group tw-group/card tw-relative' {...(isVideo ? hoverProps : {})}>
      <AspectRatio ratio='3x4' rounded='xl' centered={false}>
        {/* Thumbnail — always rendered as base layer */}
        {thumbnailUrl && (
          <Image
            src={thumbnailUrl}
            alt={humanReadableName}
            fill
            className='tw-object-cover'
            sizes={sizes("90vw", { lg: "33vw" })}
            placeholder={blurDataURL ? "blur" : "empty"}
            blurDataURL={blurDataURL ?? undefined}
          />
        )}

        {/* Video — only for video-modality, hover-to-play */}
        {isVideo && (
          <video
            ref={videoRef}
            className='tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover tw-opacity-0 group-hover/card:tw-opacity-100 tw-transition-opacity tw-duration-500'
            muted
            loop
            playsInline
            preload='metadata'
            src={contentUrl}
          />
        )}

        {/* Modality icon — top right */}
        <div className='tw-absolute tw-top-4 tw-right-4 tw-z-10 tw-rounded-full tw-bg-black/40 tw-p-2 tw-backdrop-blur-sm'>
          {modality === "video" ? (
            <IconVideo className='tw-size-4 tw-text-white' />
          ) : (
            <IconImage className='tw-size-4 tw-text-white' />
          )}
        </div>

        {/* Noise texture overlay */}
        <Noise className='tw-pointer-events-none tw-absolute tw-inset-0 tw-opacity-60' />

        {/* Gradient scrim for text readability */}
        <div className='tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-gradient-to-b tw-from-transparent tw-to-black/50' />

        {/* Overlay text — bottom-aligned */}
        <div className='tw-absolute tw-inset-0 tw-flex tw-flex-col tw-justify-end tw-p-6 md:tw-p-8'>
          <p className='tw-text-white tw-type-xl tw-mb-3'>
            <BaseLink href={`/${slug}`} className='after:tw-absolute after:tw-inset-0'>
              {humanReadableName}
            </BaseLink>
            <CardLinkArrow className='tw-text-white/80 tw-inline tw-align-middle tw-ml-1' />
          </p>
          {shortDescription && <p className='tw-text-white/60 tw-type-base tw-line-clamp-2'>{shortDescription}</p>}
        </div>
      </AspectRatio>
    </div>
  );
}
